import { liveQuery } from "dexie";
import { useCallback, useEffect, useMemo, useState } from "react";

import { getGatewayBaseUrl } from "../runtime/config";
import { PosReceivingGatewayClient } from "../runtime/gateway/receiving-client";
import { PosSalesHistoryGatewayClient } from "../runtime/gateway/sales-history-client";
import { PosShiftCloseGatewayClient } from "../runtime/gateway/shift-close-client";
import { readLocalEmployeeSession, type LocalEmployeeSessionState } from "../runtime/local-store/employee-session";
import {
  saveReceivingQueue,
  saveSalesHistory,
  saveShiftCloseRuntime,
} from "../runtime/local-store/secondary-runtime-cache";
import { readTerminalCredential, type TerminalCredentialState } from "../runtime/local-store/terminal-state";
import { getPosRuntimePerformanceProfile } from "../runtime/performance/runtime-performance-profile";
import { useRuntimeAutoSync } from "../runtime/sync/use-runtime-auto-sync";

type Snapshot = { terminal: TerminalCredentialState | null; session: LocalEmployeeSessionState | null };

// Secondary reads are not part of the every-click sales path. Keep a warm local copy
// without turning these larger read models into a 5-second polling load.
const SECONDARY_SYNC_INTERVAL_MS = 30_000;

function SecondaryReadRuntimeWorker() {
  const [snapshot, setSnapshot] = useState<Snapshot>({ terminal: null, session: null });
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const subscription = liveQuery(async () => ({
      terminal: await readTerminalCredential(),
      session: await readLocalEmployeeSession(),
    })).subscribe({ next: setSnapshot, error: () => setSnapshot({ terminal: null, session: null }) });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  const terminal = snapshot.terminal;
  const session = snapshot.session;
  const identity = terminal?.identity ?? null;
  const actor = session?.actor ?? null;
  const sameScope = Boolean(
    identity && actor
    && actor.organization_id === identity.organization_id
    && actor.branch_id === identity.branch_id
    && actor.node_id === identity.node_id
    && actor.runtime_session_status === "ACTIVE",
  );
  const baseUrl = getGatewayBaseUrl();
  const clients = useMemo(() => baseUrl ? {
    receiving: new PosReceivingGatewayClient({ baseUrl }),
    history: new PosSalesHistoryGatewayClient({ baseUrl }),
    shift: new PosShiftCloseGatewayClient({ baseUrl }),
  } : null, [baseUrl]);
  const triggerKey = sameScope && identity && actor
    ? `${identity.organization_id}:${identity.branch_id}:${identity.node_id}:${actor.runtime_session_id}`
    : "";

  const sync = useCallback(async () => {
    if (!clients || !identity || !actor || !terminal?.deviceSecret || !session?.sessionToken || !sameScope || !online) return;
    const deviceSecret = terminal.deviceSecret;
    const sessionToken = session.sessionToken;
    const jobs: Promise<void>[] = [];

    if (actor.permission_codes.includes("stock.receive.purchase")) {
      jobs.push((async () => {
        const result = await clients.receiving.queue({ deviceSecret, sessionToken });
        if (result.ok && result.data) await saveReceivingQueue(identity, result.data);
      })());
    }

    if (actor.permission_codes.includes("commerce.view")) {
      jobs.push((async () => {
        const result = await clients.history.list({
          deviceSecret,
          sessionToken,
          businessDate: null,
          search: null,
          limit: 50,
          offset: 0,
        });
        if (result.ok && result.data) await saveSalesHistory(identity, result.data);
      })());
    }

    if (actor.permission_codes.includes("pos.shift.close")) {
      jobs.push((async () => {
        const result = await clients.shift.runtime(deviceSecret, sessionToken);
        if (result.ok && result.data) await saveShiftCloseRuntime(identity, result.data);
      })());
    }

    await Promise.allSettled(jobs);
  }, [actor, clients, identity, online, sameScope, session?.sessionToken, terminal?.deviceSecret]);

  useRuntimeAutoSync({
    enabled: Boolean(triggerKey && online && clients),
    sync,
    triggerKey,
    intervalMs: SECONDARY_SYNC_INTERVAL_MS,
  });

  return null;
}

export function SecondaryReadRuntimeHost() {
  const profile = getPosRuntimePerformanceProfile();

  // STANDARD keeps the existing cache-warming behavior exactly as-is. CONSTRAINED
  // skips only this optional background worker; the receiving, sales-history and
  // shift-close surfaces remain mounted and load their own local/canonical data when used.
  if (!profile.secondaryReadWarmupEnabled) return null;
  return <SecondaryReadRuntimeWorker />;
}
